import { isStaleChunkError } from '../ErrorBoundary';
import { clearStaleBuildState } from '../../utils/sw-register';

interface ErrorFallbackProps {
  error?: Error | null;
  onRetry?: () => void;
}

const STALE_CHUNK_RELOAD_KEY = 'hsy:stale-chunk-reload-signature';

async function hardReload() {
  // Clear the once-per-signature guard so the auto-reload path can fire again
  // on the next visit if needed, then bust the SW + build caches.
  try { window.sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY); } catch {}
  await clearStaleBuildState();
  window.location.reload();
}

export function ErrorFallback({ error, onRetry }: ErrorFallbackProps) {
  const stale = isStaleChunkError(error ?? null);
  const handleTryAgain = stale
    ? () => { void hardReload(); }
    : (onRetry ?? (() => window.location.reload()));

  return (
    <div className="min-h-[50vh] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-sm text-center shadow-sm">
        <span className="text-5xl mb-4 block">🙈</span>
        <h2 className="text-lg font-bold text-gray-800 mb-2">
          Oops! Something went wrong
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          {stale
            ? "We just shipped an update. Reload to grab the latest version."
            : "Don't worry, your progress is saved! Let's try that again."}
        </p>
        {/* Shown for stale-chunk errors too: the message names the exact
            failing module URL, which is the difference between "reload roulette"
            and a one-screenshot diagnosis (see 2026-07-14 /create HTTP-cache
            poisoning incident). */}
        {error && (
          <p className="text-xs text-gray-400 mb-4 font-mono break-all">
            {error.message}
          </p>
        )}
        <div className="flex gap-3 justify-center">
          <button
            onClick={handleTryAgain}
            className="px-5 py-2.5 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 transition-colors"
          >
            {stale ? 'Reload' : 'Try Again'}
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}
